/**
 * Device capability probes for the WebAuthn picker.
 *
 * The transport picker decides which cards to enable based on TWO
 * independent signals:
 *
 *   1. The user's enrolled credentials (server-side: which transport
 *      tags are on file → if zero internal-transport credentials,
 *      "Use this device" can't sign them in regardless of device
 *      capability). This lives in `isTransportAvailable` keyed on
 *      `availableTransports` from /identify.
 *
 *   2. The current device's capabilities (browser-side: is Windows
 *      Hello / Touch ID / Android biometric actually configured?
 *      Otherwise "Use this device" can't ENROLL either, even on a
 *      fresh user with no credentials yet — the OS reports "no
 *      platform authenticator available" and the WebAuthn create()
 *      falls back to a security-key prompt or fails outright). This
 *      file owns probe #2.
 *
 * The two checks combine in the picker — a card is enabled only
 * when BOTH pass. That's what stops the canonical "user picks
 * 'this device' on a Windows machine without Hello configured →
 * sees a security-key prompt → cancels → confused" failure mode.
 *
 * Scope: two probes ship today. `probePlatformAuthenticator` answers
 * "can this device produce/consume an internal-transport credential?"
 * `probeHybridFeasibility` (added 2026-05-08 after the Azure-VM
 * dead-end incident) answers "is the cross-device QR path even
 * possible from this device?" — Bluetooth Low Energy is the
 * load-bearing transport for caBLE proximity verification, so
 * its absence is a hard block on the phone-QR ceremony, not a
 * "might work" hint. Three signals layer:
 *
 *   1. `navigator.bluetooth?.getAvailability()` (Chromium only —
 *      Safari/Firefox return `null = permissive`).
 *   2. UA + remote-session hints (Windows + RDP/AVD/Bastion
 *      indicators in the UA → known no-BLE).
 *   3. localStorage-backed "phone path failed N times" learned
 *      counter — promoted to a hard `false` after two confirmed
 *      hybrid-fast-fails on the same device.
 *
 * Conditional-mediation has its own probe below for the autofill
 * UX. See docs/product-areas.md
 * for the architectural rationale and the seven-item plan this
 * file slots into.
 */

/**
 * Probe the current device's platform-authenticator availability.
 *
 * Wraps `PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()`
 * with full SSR / missing-API / exception handling.
 *
 * Returns:
 *   - `true`  — UV-capable platform authenticator confirmed (Windows
 *               Hello PIN or biometric, Touch ID, Face ID, Android
 *               biometric, ChromeOS PIN, etc.). Safe to offer "Use
 *               this device" for both sign-in and enrollment.
 *   - `false` — OS reports no UV-capable platform authenticator. The
 *               browser will fall back when "Use this device" is
 *               picked, so consumers should disable the card and
 *               explain why.
 *   - `null`  — couldn't determine (SSR, missing API, exception).
 *               Consumers treat this as PERMISSIVE — render the card
 *               enabled. Better to show a working option that turns
 *               out to fail than to disable a working option because
 *               the probe was slow.
 *
 * Result is memoised at module scope: the API has no observable
 * side effects and the value is stable for the page's lifetime
 * (capability-state changes during a session are rare; users with
 * a real reason to re-probe — say, they enabled Hello mid-session
 * — refresh the page). Test-only callers wanting a fresh probe can
 * use `_resetPlatformAuthenticatorCacheForTests`.
 */
export async function probePlatformAuthenticator(): Promise<boolean | null> {
  if (cachedProbe !== null) return cachedProbe;
  cachedProbe = doProbe();
  return cachedProbe;
}

let cachedProbe: Promise<boolean | null> | null = null;

async function doProbe(): Promise<boolean | null> {
  // SSR / non-browser context. The picker re-runs the probe on
  // hydration via useEffect, so returning null here is the right
  // "we don't know yet — be permissive" answer rather than a hard
  // false that would disable the card on first paint.
  if (typeof window === "undefined") return null;
  // Older browsers that pre-date the WebAuthn standard, or contexts
  // where window.PublicKeyCredential is shadowed by a stub. The
  // optional-chain handles both "PublicKeyCredential is not on
  // window" and "the static method isn't on PublicKeyCredential."
  // No `as unknown as` coercion needed — modern TypeScript DOM lib
  // types this method correctly.
  if (
    typeof window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable !==
    "function"
  ) {
    return null;
  }
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    // The spec lists no documented exceptions, but Firefox in private
    // mode and some hardened-browser configurations do throw. Same
    // permissive-null answer rather than crashing the picker.
    return null;
  }
}

/**
 * Test-only escape hatch: drop the cached probe so a fresh
 * `probePlatformAuthenticator()` call re-evaluates the API state.
 * Production code MUST NOT call this — capability changes are
 * rare enough that a page refresh is the right invalidation
 * trigger.
 */
export function _resetPlatformAuthenticatorCacheForTests(): void {
  cachedProbe = null;
}

// ── Conditional-mediation probe ────────────────────────────────────────

/**
 * Probe whether the browser supports `mediation: 'conditional'`
 * on `navigator.credentials.get` (passkey autofill).
 *
 * Same tri-valued contract as `probePlatformAuthenticator`:
 *
 *   - `true`  — the API exists and reports support; conditional
 *               UI can be shown.
 *   - `false` — the API exists and explicitly reports no support
 *               (rare in practice but possible on older Edge /
 *               older Safari).
 *   - `null`  — couldn't determine (SSR, missing API surface,
 *               method threw). Consumers treat this as
 *               PERMISSIVE — render the autofill hint and let
 *               the actual `.get({ mediation: 'conditional' })`
 *               call decide; the spec already guarantees that
 *               unsupported environments fall back to a no-op
 *               so a permissive null is safe.
 *
 * The webauthn-flow module's older `supportsConditionalMediation`
 * collapses null into false; this probe keeps the three states
 * distinct so telemetry can record "unknown" instead of
 * misreporting it as "no".
 */
export async function probeConditionalMediation(): Promise<boolean | null> {
  if (cachedConditional !== null) return cachedConditional;
  cachedConditional = doConditionalProbe();
  return cachedConditional;
}

let cachedConditional: Promise<boolean | null> | null = null;

async function doConditionalProbe(): Promise<boolean | null> {
  if (typeof window === "undefined") return null;
  if (typeof window.PublicKeyCredential === "undefined") return null;
  const fn = (
    window.PublicKeyCredential as {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    }
  ).isConditionalMediationAvailable;
  if (typeof fn !== "function") return null;
  try {
    return await fn();
  } catch {
    return null;
  }
}

/** Test-only: drop the cached conditional-mediation probe. */
export function _resetConditionalMediationCacheForTests(): void {
  cachedConditional = null;
}

// ── Hybrid (phone-QR) feasibility probe ────────────────────────────────

/**
 * localStorage key holding a small JSON record of recent phone-path
 * fast-fail observations on this device. Two strikes promotes the
 * device to "hybrid_unreachable" for subsequent probes — the user
 * has already discovered Chrome's "your device can't be used"
 * message twice, the third time we save them the round trip.
 *
 * Persisted across sessions because the underlying capability
 * (Bluetooth radio absent / disabled at OS level) is also stable
 * across sessions. Cleared explicitly when:
 *
 *   - the user clicks "I've set it up — try again" on the deep-link
 *     wizard (see invalidateProbeCache below), OR
 *   - a phone-path ceremony completes successfully (the counter is
 *     reset in the loginflow runner on positive proof of capability).
 *
 * Out-of-band tampering is a non-issue: a user clearing this key in
 * DevTools just downgrades the next probe call to whatever the
 * underlying API + UA hint say. There's no security boundary here.
 */
const HYBRID_FAILURE_KEY = "pureid.hybrid_failures.v1";

interface HybridFailureRecord {
  count: number;
  /** ISO-8601 of the most recent fast-fail. We don't act on this
   *  yet — captured so a future "expire after 30 days" sweep can
   *  be added without a localStorage migration. */
  lastAt: string;
}

/**
 * Failure-record entries older than this are stale and reset
 * to zero. The Bluetooth-radio capability of a device is
 * relatively stable, but not eternal — users replace
 * laptops, swap out USB BLE dongles, move VMs to different
 * hosts. 30 days is a balance between "the learned signal
 * keeps protecting against repeat dead-ends" and "an old
 * failure on a different physical machine doesn't haunt the
 * user forever." Kept as an exported constant so the test
 * suite can fast-forward without monkey-patching Date.
 */
export const HYBRID_FAILURE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Read the persisted failure record. Tolerant of corrupt /
 * pre-existing values — any parse failure resets to a fresh
 * record so the probe can never be poisoned by a malformed
 * entry.
 *
 * Stale records (older than HYBRID_FAILURE_TTL_MS) are
 * returned as a fresh zero record, AND the underlying
 * storage is rewritten so the probe doesn't keep doing the
 * stale-check on every call. Side-effecting this read is
 * acceptable because:
 *   - The probe runs once per page session (memoised), so
 *     the rewrite happens at most once per session.
 *   - The alternative — running a separate sweep job on a
 *     timer — adds complexity for negligible gain.
 */
function readHybridFailures(): HybridFailureRecord {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return { count: 0, lastAt: "" };
  }
  try {
    const raw = window.localStorage.getItem(HYBRID_FAILURE_KEY);
    if (!raw) return { count: 0, lastAt: "" };
    const parsed = JSON.parse(raw) as Partial<HybridFailureRecord>;
    if (typeof parsed.count !== "number" || parsed.count < 0) {
      return { count: 0, lastAt: "" };
    }
    const lastAt = typeof parsed.lastAt === "string" ? parsed.lastAt : "";
    // Expiry sweep — see HYBRID_FAILURE_TTL_MS commentary.
    if (lastAt) {
      const ts = Date.parse(lastAt);
      if (Number.isFinite(ts) && Date.now() - ts > HYBRID_FAILURE_TTL_MS) {
        try {
          window.localStorage.removeItem(HYBRID_FAILURE_KEY);
        } catch {
          /* quota / private mode — ignore */
        }
        return { count: 0, lastAt: "" };
      }
    }
    return {
      count: Math.min(parsed.count, 99), // clamp; see commentary
      lastAt,
    };
  } catch {
    return { count: 0, lastAt: "" };
  }
}

function writeHybridFailures(rec: HybridFailureRecord): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(HYBRID_FAILURE_KEY, JSON.stringify(rec));
  } catch {
    // Quota exceeded / private mode / permission denied — silently
    // accept the loss. The probe falls back to the Bluetooth API +
    // UA hint with no learned signal, which is the same posture as
    // a fresh device.
  }
}

/**
 * Public hook: report a confirmed phone-path ceremony fast-fail.
 *
 * Called from the loginflow runner when a `NotAllowedError` arrives
 * on a phone-targeted ceremony in under 800ms (the classifier's
 * "fast-fail" threshold — the user never saw a real BLE pairing
 * attempt). Increments the counter; the next probeHybridFeasibility
 * call returns false once the threshold (2) is crossed.
 *
 * Two strikes is deliberate: a single fast-fail can be transient
 * (Bluetooth disabled then enabled, phone temporarily out of range),
 * but a second one on the same device makes the no-BLE conclusion
 * effectively certain. False positives here are recoverable via
 * invalidateProbeCache().
 */
export function recordHybridFastFail(): void {
  const cur = readHybridFailures();
  writeHybridFailures({
    count: cur.count + 1,
    lastAt: new Date().toISOString(),
  });
  // Layer 2 (same-session) counter increments alongside the
  // persistent counter. The values are independent — the
  // session count is what gates Layer 2 of the next probe;
  // the persistent count is what gates Layer 1.
  writeSessionHybridFailureCount(readSessionHybridFailureCount() + 1);
}

/**
 * Public hook: report a confirmed phone-path ceremony success.
 *
 * The persistent counter DECREMENTS by one (clamped at zero)
 * rather than resetting to zero. Decrement-on-success is more
 * honest about the "device on the edge" case: a user with one
 * past failure who then succeeds has demonstrated BLE works
 * AT LEAST ONCE on this device, but if they immediately fail
 * again afterwards, we want THAT failure to count as a fresh
 * strike (count=1), not as a second strike (count=2 which
 * would trip the threshold). A reset-to-zero would lose that
 * historical signal unfairly.
 *
 * The session-storage counter resets fully (not decrement) on
 * a confirmed success — within a single page session, a
 * working ceremony is sufficient evidence that the radio +
 * pairing path works for the current configuration.
 */
export function recordHybridSuccess(): void {
  const cur = readHybridFailures();
  if (cur.count > 0) {
    writeHybridFailures({
      count: cur.count - 1,
      // Keep lastAt fresh — we just observed a probe-relevant
      // event on this device, even if it was positive.
      lastAt: new Date().toISOString(),
    });
  }
  writeSessionHybridFailureCount(0);
}

/** Threshold beyond which the learned signal flips the probe to
 *  false. Two strikes — see commentary on `recordHybridFastFail`. */
const HYBRID_FAILURE_THRESHOLD = 2;

let cachedHybrid: Promise<boolean | null> | null = null;

/**
 * Probe whether the cross-device (phone QR) WebAuthn ceremony has
 * a chance of succeeding from this device.
 *
 * Tri-valued contract identical to the platform probe:
 *
 *   - `true`  — the API confirms a Bluetooth radio is present AND
 *               none of the negative heuristics (UA hints, learned
 *               failures) flag this device.
 *   - `false` — Bluetooth API explicitly reports unavailable, OR a
 *               UA-detected remote-session host is in use, OR the
 *               localStorage failure counter has crossed the
 *               threshold. Any one of these is sufficient.
 *   - `null`  — couldn't determine. Safari/Firefox don't expose
 *               `navigator.bluetooth`, so this is the common case
 *               outside Chromium. Consumers treat this as
 *               PERMISSIVE — render the QR card enabled. The
 *               classifier will fall the user back to a clear
 *               post-failure state if the ceremony does fail; we
 *               prefer that over hiding a working option on
 *               browsers we can't probe.
 *
 * Memoised at module scope, same as the other probes. Test-only
 * resets via `_resetHybridFeasibilityCacheForTests`.
 */
export async function probeHybridFeasibility(): Promise<boolean | null> {
  if (cachedHybrid !== null) return cachedHybrid;
  cachedHybrid = doHybridProbe();
  return cachedHybrid;
}

async function doHybridProbe(): Promise<boolean | null> {
  if (typeof window === "undefined") return null;

  // Layer 1 — learned signal trumps everything. If the user has
  // demonstrably failed phone-path ceremonies on this device twice,
  // we know the answer regardless of what the Bluetooth API
  // currently says.
  const failures = readHybridFailures();
  if (failures.count >= HYBRID_FAILURE_THRESHOLD) {
    return false;
  }

  // Layer 2 — UA-derived hint. The classic PureID no-BLE host is a
  // Windows VM accessed via RDP / Azure Bastion / Citrix / AVD —
  // Windows + Chrome with no platform Bluetooth radio. We can't
  // detect "no radio" reliably, but the UA shape is suggestive.
  // Be conservative: only flag the strongest hints to avoid false
  // positives on users with locked-down corporate laptops that
  // happen to run a similar UA.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (looksLikeRemoteWindowsSession(ua)) {
    return false;
  }

  // Layer 3 — Bluetooth Web API. Chromium gates this behind the
  // Permissions Policy AND user activation in some contexts; we
  // get either `false` (no radio / permission denied) or a thrown
  // exception in the latter case. Both → null = permissive, since
  // a genuine "Chromium can't tell us" is closer to "we don't
  // know" than to "no radio". Layer 1 catches the user's actual
  // failures regardless.
  const blob = (
    navigator as unknown as {
      bluetooth?: { getAvailability?: () => Promise<boolean> };
    }
  ).bluetooth;
  if (typeof blob?.getAvailability !== "function") {
    return null;
  }
  try {
    const ok = await blob.getAvailability();
    // `false` here is high-confidence — Chromium has explicit
    // signal that the OS reports no usable radio.
    return ok ? true : false;
  } catch {
    return null;
  }
}

/**
 * sessionStorage key recording every confirmed phone-path
 * fast-fail in the CURRENT browser session. Distinct from the
 * persistent counter (HYBRID_FAILURE_KEY in localStorage):
 * this one is intentionally short-lived and exists to give
 * Layer 2 a real signal on the SAME page session as the
 * failure happened. A user who fast-fails the QR ceremony
 * once on this session lands on the help screen; if they
 * navigate back and try the picker again, Layer 2 returns
 * true (we've already seen one fast-fail this session) and
 * the next probe is "false" without waiting for the
 * persistent two-strikes threshold to trip.
 *
 * Cleared on a successful phone-path ceremony (record­
 * HybridSuccess) AND on invalidateProbeCache. Not persisted
 * across page reloads — the persistent counter (Layer 1)
 * carries the load-bearing learned signal across sessions.
 */
const HYBRID_SESSION_FAILURE_KEY = "pureid.hybrid_failures.session.v1";

function readSessionHybridFailureCount(): number {
  if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
    return 0;
  }
  try {
    const raw = window.sessionStorage.getItem(HYBRID_SESSION_FAILURE_KEY);
    if (!raw) return 0;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(n, 99);
  } catch {
    return 0;
  }
}

function writeSessionHybridFailureCount(n: number): void {
  if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
    return;
  }
  try {
    if (n <= 0) {
      window.sessionStorage.removeItem(HYBRID_SESSION_FAILURE_KEY);
    } else {
      window.sessionStorage.setItem(HYBRID_SESSION_FAILURE_KEY, String(n));
    }
  } catch {
    /* private mode / quota — fall back to no-op */
  }
}

/**
 * Layer 2 — UA + same-session failure heuristic.
 *
 * The original design called for a UA-only "is this a remote
 * Windows session" detector, but realistic UA-only signals
 * have unacceptably high false-positive rates: a corporate
 * laptop running Chrome on Windows looks identical to a
 * Bastion / AVD session. False positives disable the QR
 * option for users who could legitimately have used it, which
 * is a UX regression.
 *
 * The honest signal is "we've ALREADY seen a hybrid fast-fail
 * on this same page session, AND the UA is plausibly remote-
 * Windows-shaped." First strike (no session signal) → permissive
 * null; second strike on the same session → flagged false. This
 * is strictly tighter than the persistent two-strikes counter
 * (Layer 1, which spans sessions) and gets the user to the
 * "no working method" screen on the second attempt of a single
 * session rather than waiting for them to come back tomorrow.
 *
 * The Layer 1 + Layer 2 + Layer 3 stack:
 *
 *   Layer 1 (persistent localStorage, two strikes across
 *            sessions)        — catches the chronic case
 *   Layer 2 (sessionStorage,
 *            one strike + UA shape)
 *                              — catches the same-session
 *                                second attempt before the
 *                                persistent counter trips
 *   Layer 3 (navigator.bluetooth.getAvailability)
 *                              — Chromium-only, high-confidence
 *                                positive when present
 *
 * Returns true only when BOTH the session counter has at least
 * one fast-fail AND the UA matches the loose remote-Windows
 * shape; either alone is not enough.
 */
function looksLikeRemoteWindowsSession(ua: string): boolean {
  if (!ua) return false;
  // UA shape: Windows + Chrome/Edge/Firefox, no mobile-platform
  // indicators. Mobile UAs are NOT remote sessions in our scope
  // (they have built-in BLE radios) so we exclude them.
  const isWindows = /Windows NT /i.test(ua);
  const isMobile = /(iPhone|iPad|iPod|Android|Mobile)/i.test(ua);
  const looksDesktop = isWindows && !isMobile;
  if (!looksDesktop) return false;

  // Combined with at least one in-session fast-fail. Until that
  // first failure, every Windows desktop user looks the same and
  // we don't want to disable the QR option pre-emptively.
  return readSessionHybridFailureCount() >= 1;
}

/** Test-only: drop the cached hybrid-feasibility probe AND
 *  every persistence channel (localStorage failure record,
 *  sessionStorage same-session counter). Production code
 *  must use `invalidateProbeCache()` instead, which keeps
 *  semantics documented (user explicitly returned from
 *  system settings). */
export function _resetHybridFeasibilityCacheForTests(): void {
  cachedHybrid = null;
  if (typeof window !== "undefined") {
    try {
      window.localStorage?.removeItem(HYBRID_FAILURE_KEY);
    } catch {
      /* ignore */
    }
    try {
      window.sessionStorage?.removeItem(HYBRID_SESSION_FAILURE_KEY);
    } catch {
      /* ignore */
    }
  }
}

// ── Cache invalidation ─────────────────────────────────────────────────

/**
 * Invalidate ALL capability probe caches.
 *
 * Module-scope memoisation is right for normal use — a page's
 * lifetime is short and the underlying capabilities don't change
 * mid-session. The exception is the deep-link setup wizard:
 *
 *   1. User hits the platform_unavailable error.
 *   2. We surface a "Set up Windows Hello" button that fires
 *      `ms-settings:signinoptions`.
 *   3. User completes setup in a different OS-level window.
 *   4. User returns to PureID and clicks "I've set it up —
 *      try again".
 *
 * Step 4 needs to re-probe `isUserVerifyingPlatformAuthenticatorAvailable`
 * because the OS state genuinely changed. Without this helper the
 * cached `false` from step 1 sticks until full page reload, and
 * the re-try button silently no-ops.
 *
 * Also clears the conditional-mediation cache and the learned
 * hybrid-failure counter — the latter because if a user is
 * deliberately re-trying after fixing their environment, any
 * accumulated failure signal is stale by definition.
 */
export function invalidateProbeCache(): void {
  cachedProbe = null;
  cachedConditional = null;
  cachedHybrid = null;
  privateModeCache = undefined;
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    try {
      window.localStorage.removeItem(HYBRID_FAILURE_KEY);
    } catch {
      /* quota / private mode — ignore */
    }
  }
  // Layer 2 session counter clears too. The user has explicitly
  // declared "I've fixed something" by clicking the retry button,
  // so the session-scoped failure record is no longer valid.
  writeSessionHybridFailureCount(0);
}

// ── Aggregate capability snapshot ──────────────────────────────────────

/**
 * The complete browser-side capability picture at a single moment.
 * Consumed by:
 *
 *   - The transport picker, which uses it to enable/disable cards
 *     and surface inline help text.
 *   - The classifier (`classifyCeremonyFailure`), which uses it as
 *     evidence to disambiguate a NotAllowedError fast-fail (no
 *     authenticator) from a user-driven cancel.
 *   - The telemetry reporter, which mirrors it 1:1 onto the wire
 *     so operators reading server logs see the same state the
 *     browser saw.
 *
 * Tri-valued probes (`platformAuthenticator`, `conditionalMediation`)
 * preserve the "couldn't determine" state explicitly. The two
 * non-pointer fields are not tri-valued because their values are
 * proven by the page already running this code: WebAuthn presence
 * is a static check, secure-context is exposed unambiguously by
 * `window.isSecureContext`.
 */
export interface CapabilitySnapshot {
  webauthn: boolean;
  secureContext: boolean;
  platformAuthenticator: boolean | null;
  conditionalMediation: boolean | null;
  /** Cross-device (phone QR) ceremony feasibility. Tri-valued
   *  with the same permissive-null contract as the other fields.
   *  Added 2026-05-08 for the no-BLE-environment dead-end class
   *  (Azure VM / Citrix / AVD). See `probeHybridFeasibility`. */
  hybridFeasibility: boolean | null;
  /** Private / InPrivate / Incognito browsing mode detection
   *  (B-5, 2026-05-11). Most browsers disable cross-origin
   *  passkey storage in private mode, causing silent
   *  NotAllowedError mid-ceremony that the 13-code classifier
   *  maps to a generic "no authenticator available" message
   *  that doesn't help the user.
   *
   *  Tri-valued like the other async probes:
   *    `true`  → confirmed private mode (writes failed)
   *    `false` → confirmed normal mode (writes succeeded)
   *    `null`  → couldn't determine (SSR, exception in probe)
   *
   *. The check is cheap (one localStorage write/remove pair)
   *  and the result is module-cached so the probe runs once
   *  per page load.
   */
  privateMode: boolean | null;
}

/**
 * A best-effort CapabilitySnapshot for callers that need a
 * concrete snapshot before the async probe resolves — e.g. a
 * ceremony fails while `probeCapabilities()` is still in flight,
 * but `classifyCeremonyFailure` requires a non-null snapshot.
 *
 * Synchronously detects the two cheap booleans and leaves the
 * async-only fields `null` ("couldn't determine" — the
 * classifier treats null permissively). In SSR (no `window`)
 * both booleans are false, matching the old inline fallback.
 *
 * Single source of truth: the enrollment wizard, the portal
 * add-a-passkey dialog, and recovery re-enrollment all use this
 * instead of hand-rolling the same six-field literal.
 */
/**
 * hasWebAuthn — THE WebAuthn availability predicate.
 *
 * This is deliberately the same truthiness check the ceremony
 * runners (`runAssertion` / `runAttestation` in webauthn-flow.ts)
 * gate on before throwing `not_supported`, so every surface that
 * reads `capability.webauthn === false` is reporting exactly the
 * condition the runner enforces — never a probabilistic probe.
 * The no-WebAuthn preflight rescue (NoWebAuthnSurface) is allowed
 * to pre-block on this value for precisely that reason; keep the
 * three sites on this one function so they can never diverge.
 * Pinned by device-capabilities.test.ts ("predicate unity").
 *
 * NOTE: absence of the API is a guarantee the ceremony fails, but
 * presence is NOT a guarantee it succeeds — iOS WKWebView and
 * Android WebView expose `PublicKeyCredential` and still reject at
 * ceremony time unless the embedding app cooperates. That class is
 * caught post-failure (the `not_supported` help-step routing), not
 * here.
 */
export function hasWebAuthn(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export function defaultCapabilitySnapshot(): CapabilitySnapshot {
  return {
    webauthn: hasWebAuthn(),
    secureContext:
      typeof window !== "undefined" && window.isSecureContext === true,
    platformAuthenticator: null,
    conditionalMediation: null,
    hybridFeasibility: null,
    privateMode: null,
  };
}

/**
 * Run the full capability probe set. Each underlying probe is
 * memoised, so calling this on every page (or on every error)
 * is cheap. The two async probes run in parallel because they
 * have no dependency on each other.
 */
export async function probeCapabilities(): Promise<CapabilitySnapshot> {
  if (typeof window === "undefined") {
    return defaultCapabilitySnapshot();
  }
  const webauthn = hasWebAuthn();
  const secureContext =
    typeof window.isSecureContext === "boolean" ? window.isSecureContext : false;
  const [platformAuthenticator, conditionalMediation, hybridFeasibility] =
    await Promise.all([
      probePlatformAuthenticator(),
      probeConditionalMediation(),
      probeHybridFeasibility(),
    ]);
  return {
    webauthn,
    secureContext,
    platformAuthenticator,
    conditionalMediation,
    hybridFeasibility,
    privateMode: probePrivateMode(),
  };
}

/**
 * probePrivateMode — B-5 mitigation.
 *
 * Detects whether the browser is in a private / incognito /
 * InPrivate mode by attempting a localStorage write. Every
 * major browser raises a `SecurityError` / `QuotaExceededError`
 * exception (or silently fails the write) when storage is
 * unavailable, which happens both in private mode AND when
 * the user has disabled site data — both are situations where
 * cross-origin passkey storage will fail mid-ceremony, so the
 * detection is correct for our use case regardless of which
 * specific cause triggered it.
 *
 * Returns `true` when storage is unavailable (treat as
 * private), `false` when storage works (normal mode), and
 * `null` on SSR or when the test itself throws unexpectedly.
 *
 * The result is cached at module scope so calling this on
 * every preflight is essentially free.
 */
let privateModeCache: boolean | null | undefined;

export function probePrivateMode(): boolean | null {
  if (typeof window === "undefined") return null;
  if (privateModeCache !== undefined) return privateModeCache;
  try {
    const k = "__pureid_pm_probe__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    privateModeCache = false;
    return false;
  } catch {
    privateModeCache = true;
    return true;
  }
}

// Test-only seam: clear the module-scope cache so each
// vitest case starts from a clean slate.
export function _resetPrivateModeCacheForTests(): void {
  privateModeCache = undefined;
}

/**
 * isMobileBrowser — coarse "is this a phone/tablet" detector.
 *
 * Lives here with the other capability probes (not inline in the
 * picker) because that's what it is: a device-capability signal.
 * Phase G uses it to suppress the PureID-QR enrollment card on
 * mobile — a phone scanning a QR to enroll *itself* is a category
 * error (the user is already on a passkey-capable device).
 *
 * Pure + dependency-free. Three layers, most-reliable first:
 *   1. userAgentData.mobile — Chromium high-entropy signal.
 *   2. UA regex — Safari/Firefox fallback (no userAgentData).
 *   3. iPadOS-desktop-mode — since iPadOS 13, Safari on iPad
 *      reports a Mac UA by default; the reliable tell is
 *      platform≈"Mac" + maxTouchPoints > 1 (Macs have 0).
 */
export function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
    maxTouchPoints?: number;
  };
  if (typeof nav.userAgentData?.mobile === "boolean") {
    return nav.userAgentData.mobile;
  }
  if (/Mobile|Android|iPhone|iPad/i.test(nav.userAgent ?? "")) {
    return true;
  }
  if (
    typeof nav.maxTouchPoints === "number" &&
    nav.maxTouchPoints > 1 &&
    (nav.platform ?? "").toLowerCase().includes("mac")
  ) {
    return true;
  }
  return false;
}

// ── User-agent hint parsing ────────────────────────────────────────────

/**
 * A coarse, log-safe classification of the browser's reported
 * user agent. This is OPERATIONAL HINT INFORMATION — it lets a
 * support engineer reading logs see "this user was on Firefox
 * 125 / Linux" without exposing a fingerprintable subset of the
 * raw UA string and without taking a dependency on a UA-parser
 * library. The values are intentionally low-resolution.
 *
 * Wire constraints (matches the backend regex on /api/client-
 * telemetry):
 *
 *   - browser: `[A-Za-z0-9 ._-]{1,32}`
 *   - os: same shape as browser
 *   - version: `[A-Za-z0-9._-]{1,16}` (just the marketing major,
 *     no patch / build)
 *
 * Unknown / unparseable values fall back to "Unknown" / "0", both
 * inside the regex. The function NEVER throws.
 */
export interface UaHint {
  browser: string;
  os: string;
  version: string;
}

const browserMatchers: ReadonlyArray<{ name: string; pattern: RegExp; version: RegExp }> = [
  // Edge precedes Chrome because Edge UAs contain "Chrome/" too.
  { name: "Edge", pattern: /Edg\//i, version: /Edg\/(\d+)/i },
  { name: "Firefox", pattern: /Firefox\//i, version: /Firefox\/(\d+)/i },
  // Brave / Vivaldi / Opera all advertise Chrome/N — collapsing
  // them under "Chrome" is the right resolution for an operations
  // log; richer disambiguation is a fingerprinting risk we
  // explicitly don't want.
  { name: "Chrome", pattern: /Chrome\//i, version: /Chrome\/(\d+)/i },
  // Safari's marketing version lives in `Version/N`; the
  // `Safari/M` token is the WebKit build number and is not what
  // operators want.
  { name: "Safari", pattern: /Safari\//i, version: /Version\/(\d+)/i },
];

const osMatchers: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  // iPadOS BEFORE iOS so an iPad UA gets the dedicated slot
  // (chip says "iPad with Face ID or Touch ID" instead of
  // "iPhone"). Note: an iPad in desktop-mode Safari (iPadOS 13+
  // default for 12.9" + toggleable on smaller iPads) sends a
  // "Macintosh; Intel Mac OS X" UA that is INDISTINGUISHABLE
  // from a real Mac without a JS probe (navigator.maxTouchPoints
  // > 1). For now we accept that desktop-mode iPad reads as
  // Mac; the touch-mode default is the more common case and
  // sends "iPad" explicitly. Future work: probe maxTouchPoints
  // before parseUaHint and pass it through as a hint.
  { name: "iPadOS", pattern: /iPad/i },
  // iOS catches iPhone + iPod (and any future Apple handheld
  // that uses the same UA shape). Order matters: iPadOS already
  // claimed iPad above.
  { name: "iOS", pattern: /iPhone|iPod/i },
  { name: "Android", pattern: /Android/i },
  { name: "Windows", pattern: /Windows NT/i },
  { name: "macOS", pattern: /Mac OS X/i },
  // Linux is the common fallback — matches "X11; Linux x86_64"
  // and similar. Keep AFTER Android so the Android-on-Linux
  // userAgent (which contains "Linux") is classified correctly.
  { name: "Linux", pattern: /Linux/i },
];

/**
 * Parse navigator.userAgent into a coarse {browser, os, version}
 * hint. Pure function — no side effects, no globals. Pass the UA
 * string explicitly so tests don't need to stub navigator.
 */
export function parseUaHint(ua: string | null | undefined): UaHint {
  if (!ua) return { browser: "Unknown", os: "Unknown", version: "0" };

  let browser = "Unknown";
  let version = "0";
  for (const m of browserMatchers) {
    if (m.pattern.test(ua)) {
      browser = m.name;
      const v = ua.match(m.version);
      if (v && v[1]) version = v[1];
      break;
    }
  }

  let os = "Unknown";
  for (const m of osMatchers) {
    if (m.pattern.test(ua)) {
      os = m.name;
      break;
    }
  }

  // Defense-in-depth: clamp to the wire-side regex bounds.
  // The matchers above can't legitimately produce out-of-range
  // values, but a future refactor that loosens the rules
  // shouldn't let bad data reach the wire.
  if (browser.length > 32) browser = browser.slice(0, 32);
  if (os.length > 32) os = os.slice(0, 32);
  if (version.length > 16) version = version.slice(0, 16);

  return { browser, os, version };
}

// ── Push-fallback detection (WebAuthn-hostile clients) ──────────────────
//
// Some surfaces render the login page inside a restricted webview that
// cannot run a passkey ceremony and cannot hand off to a system browser
// (O365 Teams/Outlook embedded webviews, macOS Captive Network Assistant,
// eLux thin-client logon). There the user must be routed to the push (or
// QR) approval fallback instead of a WebAuthn card. See
// docs/security.md
// and its companion implementation plan.
//
// The detection contract is deliberately conservative:
//   - WebAuthn feature detection is ALWAYS the primary signal.
//   - The embedded-webview UA shape is a SECONDARY signal, consulted
//     only to confirm an already-negative platform-authenticator probe —
//     never on its own, so a normal browser with an unusual UA is never
//     forced onto the fallback.
//   - `null` probes stay PERMISSIVE: an unknown state never forces the
//     fallback (matches every other probe in this module).

/**
 * Positive, high-confidence markers that the page is running inside an
 * EMBEDDED webview / in-app browser rather than a full system browser.
 * These are host-app / webview tokens; matching one is a strong hint but,
 * per the contract above, is only ever used as a secondary signal.
 */
const embeddedWebviewMarkers: ReadonlyArray<RegExp> = [
  // Android System WebView — the canonical `; wv)` token in the UA
  // platform section (e.g. "...Build/QP1A.190711.020; wv) AppleWebKit").
  /;\s*wv\b/i,
  // Microsoft 365 embedded webviews / auth host.
  /\bTeams(Mobile)?\//i,
  /\bMSTeams\b/i,
  /Outlook-(iOS|Android|Mobile)/i,
  /MicrosoftOutlook/i,
  /MSAuthHost/i,
  /\bWXP\//i, // Office desktop shared-code marker
  /Microsoft Office/i,
  // macOS Captive Network Assistant (the WiFi captive-portal mini-browser).
  /CaptiveNetworkSupport/i,
  // eLux thin-client logon shell (best-effort token; low volume, and the
  // secondary-signal rule makes any false positive harmless).
  /\beLux\b/i,
];

/**
 * iOS / iPadOS in-app WKWebViews advertise no dedicated token — the tell
 * is an Apple WebKit *mobile* UA that lacks the `Safari/` marker a real
 * Mobile Safari always carries, and is not one of the full third-party
 * iOS browsers (Chrome=CriOS, Firefox=FxiOS, Edge=EdgiOS, Opera=OPiOS),
 * which also drop `Safari/` but ARE passkey-capable. An iPadOS device in
 * desktop-mode Safari sends a Mac UA and cannot be caught here — that
 * path is covered by the platform-authenticator probe instead.
 */
function looksLikeIosInAppWebview(ua: string): boolean {
  if (!/(iPhone|iPod|iPad)/i.test(ua)) return false;
  if (!/AppleWebKit/i.test(ua)) return false;
  if (/Safari\//i.test(ua)) return false; // real Mobile Safari
  if (/(CriOS|FxiOS|EdgiOS|OPiOS)\//i.test(ua)) return false; // full iOS browsers
  return true;
}

/**
 * Coarse "is this an embedded webview / in-app browser" detector.
 *
 * Pure function — pass the UA explicitly (mirrors `parseUaHint`), no
 * globals, never throws. Returns true for a recognised embedded-webview
 * host token OR the iOS-no-`Safari` in-app-webview shape.
 *
 * INTENDED USE: a SECONDARY disambiguator only. `shouldOfferPushFallback`
 * consults it exclusively when the platform-authenticator probe already
 * returned an explicit `false`. Do not gate security decisions on it
 * alone — UA strings are attacker-controllable and imprecise.
 */
export function looksLikeEmbeddedWebview(ua: string | null | undefined): boolean {
  if (!ua) return false;
  for (const m of embeddedWebviewMarkers) {
    if (m.test(ua)) return true;
  }
  return looksLikeIosInAppWebview(ua);
}

/**
 * Decide whether to actively OFFER (emphasise) the push / QR login
 * fallback because a passkey ceremony cannot run in this surface.
 *
 * Returns true in exactly the "WebAuthn is genuinely unusable here"
 * cases. It deliberately does NOT return true for "WebAuthn works but the
 * user has no passkey yet" — that is a credential-availability question
 * the transport picker answers from the server's `availableTransports`,
 * where push is a secondary link rather than an emphasised route.
 *
 * Contract (see the design doc):
 *   - PRIMARY signal is always WebAuthn feature detection.
 *   - The embedded-webview UA shape is SECONDARY — consulted only to
 *     confirm an already-negative platform-authenticator probe.
 *   - `null` probes are PERMISSIVE: an unknown platform-authenticator
 *     state never forces the fallback.
 *
 * Note: pass a real client-side `probeCapabilities()` snapshot. The SSR
 * `defaultCapabilitySnapshot()` reports `webauthn: false`, which would
 * (correctly) read as "no WebAuthn" — callers evaluate this after the
 * client-side probe resolves, on mount.
 *
 * @param snap the capability snapshot from `probeCapabilities()`.
 * @param ua   the raw user-agent (pass `navigator.userAgent`); optional
 *             so SSR/test callers can omit it.
 */
export function shouldOfferPushFallback(
  snap: CapabilitySnapshot,
  ua?: string | null,
): boolean {
  // 1. WebAuthn API genuinely absent — definitive, no UA needed.
  if (!snap.webauthn) return true;
  // 2. Not a secure context — WebAuthn cannot run here at all.
  if (!snap.secureContext) return true;
  // 3. API present but the platform authenticator is EXPLICITLY
  //    unavailable AND the surface looks like an embedded webview. Both
  //    are required: the `=== false` guard preserves the permissive-null
  //    contract (unknown never triggers), and the UA check stays strictly
  //    secondary (never fires on its own).
  if (snap.platformAuthenticator === false && looksLikeEmbeddedWebview(ua)) {
    return true;
  }
  // Otherwise WebAuthn is usable (or its state is unknown/permissive), or
  // this isn't a recognised hostile webview — do not emphasise push.
  return false;
}
